import { useState, useEffect, useCallback } from "react";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subMonths, subWeeks,
} from "date-fns";
import {
  CalendarIcon, Loader2, DollarSign, Download, Wallet, Check, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { staffColor } from "@/lib/staffColors";
import {
  getPayrollLedger, recordPayment, getPayments, deletePayment, payAll,
} from "@/lib/api";

const PRESETS = [
  { value: "this_week", label: "This Week" },
  { value: "fortnight", label: "Fortnight" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "custom", label: "Custom" },
];

function presetRange(preset) {
  const today = new Date();
  switch (preset) {
    case "fortnight":
      return { from: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), to: endOfWeek(today, { weekStartsOn: 1 }) };
    case "this_month": return { from: startOfMonth(today), to: endOfMonth(today) };
    case "last_month": { const lm = subMonths(today, 1); return { from: startOfMonth(lm), to: endOfMonth(lm) }; }
    default: return { from: startOfWeek(today, { weekStartsOn: 1 }), to: endOfWeek(today, { weekStartsOn: 1 }) };
  }
}

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function Payroll() {
  const [preset, setPreset] = useState("this_month");
  const [range, setRange] = useState(() => presetRange("this_month"));
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);

  const [detailRow, setDetailRow] = useState(null);
  const [periodPayments, setPeriodPayments] = useState([]);
  const [payForm, setPayForm] = useState({ transfer_amount: "", cash_amount: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const [payAllOpen, setPayAllOpen] = useState(false);
  const [payingAll, setPayingAll] = useState(false);

  const start = format(range.from, "yyyy-MM-dd");
  const end = format(range.to, "yyyy-MM-dd");

  const onPreset = (v) => {
    setPreset(v);
    if (v !== "custom") setRange(presetRange(v));
  };

  const fetchGrid = useCallback(async () => {
    setLoading(true);
    try {
      setGrid(await getPayrollLedger(format(range.from, "yyyy-MM-dd"), format(range.to, "yyyy-MM-dd")));
    } catch {
      toast.error("Failed to load payroll");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    let active = true;
    Promise.resolve().then(() => { if (active) fetchGrid(); });
    return () => { active = false; };
  }, [fetchGrid]);

  const openDetail = useCallback(async (row) => {
    setDetailRow(row);
    setPayForm({ transfer_amount: "", cash_amount: "", notes: "" });
    try {
      const pays = await getPayments({ staff_id: row.staff_id });
      setPeriodPayments(pays.filter((p) => p.period_start === start && p.period_end === end));
    } catch { setPeriodPayments([]); }
  }, [start, end]);

  const handleRecordPayment = async () => {
    if (!detailRow) return;
    const transfer = parseFloat(payForm.transfer_amount) || 0;
    const cash = parseFloat(payForm.cash_amount) || 0;
    if (transfer <= 0 && cash <= 0) { toast.error("Enter a cash and/or transfer amount"); return; }
    setSubmitting(true);
    try {
      await recordPayment({
        staff_id: detailRow.staff_id, period_start: start, period_end: end,
        transfer_amount: transfer, cash_amount: cash, super_paid: 0, notes: payForm.notes,
      });
      toast.success("Settlement recorded");
      setDetailRow(null);
      fetchGrid();
    } catch {
      toast.error("Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = async (id) => {
    try {
      await deletePayment(id);
      toast.success("Payment deleted");
      setDetailRow(null);
      fetchGrid();
    } catch { toast.error("Failed to delete payment"); }
  };

  const handlePayAll = async () => {
    setPayingAll(true);
    try {
      const res = await payAll(start, end);
      if (res.paid_count > 0) toast.success(`Paid ${res.paid_count} staff — ${money(res.total_paid)} transferred`);
      else toast.info("No outstanding net balances for this period");
      setPayAllOpen(false);
      fetchGrid();
    } catch {
      toast.error("Failed to run batch payout");
    } finally {
      setPayingAll(false);
    }
  };

  const owedStaffCount = grid?.rows?.filter((r) => r.net_owed > 0).length || 0;

  const exportCSV = () => {
    if (!grid?.rows?.length) return;
    const headers = ["Staff", "Payout Type", "Sessions", "Gross Earnings", "Super", "Cash Paid Daily", "Transfer Paid", "Net Owed"];
    const lines = [headers.join(",")];
    grid.rows.forEach((r) => lines.push([
      `"${r.staff_name}"`, r.payout_type, r.total_sessions, r.gross_earnings,
      r.super_amount || 0, r.cash_deductions, r.transfer_paid, r.net_owed,
    ].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payroll_${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold text-forest flex items-center gap-2">
            <Wallet className="h-6 w-6" /> Payroll Ledger
          </h2>
          <p className="text-sage text-sm">Gross · Deductions · Net Owed for {format(range.from, "MMM d")} – {format(range.to, "MMM d, yyyy")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={preset} onValueChange={onPreset}>
            <SelectTrigger className="w-[140px]" data-testid="payroll-preset-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="bg-white" data-testid="payroll-date-range">
                <CalendarIcon className="mr-2 h-4 w-4" />{format(range.from, "MMM d")} - {format(range.to, "MMM d")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="range" selected={range} numberOfMonths={2}
                onSelect={(r) => { if (r?.from && r?.to) { setRange(r); setPreset("custom"); } else if (r?.from) setRange({ from: r.from, to: r.from }); }} />
            </PopoverContent>
          </Popover>
          <Button variant="outline" onClick={exportCSV} disabled={!grid?.rows?.length} data-testid="payroll-export-btn">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Grand totals */}
      {grid && (
        <div className={`grid grid-cols-2 gap-4 ${grid.super_enabled ? "md:grid-cols-6" : "md:grid-cols-5"}`}>
          <div className="stat-card"><p className="text-xs text-sage">Sessions</p><p className="text-xl font-heading font-bold text-forest">{grid.totals.total_sessions}</p></div>
          <div className="stat-card"><p className="text-xs text-sage">Gross Earnings</p><p className="text-xl font-heading font-bold text-forest" data-testid="grand-total-gross">{money(grid.totals.gross_earnings)}</p></div>
          {grid.super_enabled && (
            <div className="stat-card" data-testid="grand-total-super">
              <p className="text-xs text-sage">Super {grid.super_rate ? `(${grid.super_rate}%)` : ""}</p>
              <p className="text-xl font-heading font-bold text-sage">{money(grid.totals.super_amount)}</p>
              <p className="text-[10px] text-sage/70">{grid.super_mode === "deduct" ? "deducted" : "employer-paid"}</p>
            </div>
          )}
          <div className="stat-card"><p className="text-xs text-sage">Cash Paid Daily</p><p className="text-xl font-heading font-bold text-ochre">{money(grid.totals.cash_deductions)}</p></div>
          <div className="stat-card"><p className="text-xs text-sage">Transfer Paid</p><p className="text-xl font-heading font-bold text-sage">{money(grid.totals.transfer_paid)}</p></div>
          <div className="stat-card"><p className="text-xs text-sage">Net Owed</p><p className="text-xl font-heading font-bold text-terracotta" data-testid="grand-total-owed">{money(grid.totals.net_owed)}</p></div>
        </div>
      )}

      {/* Batch actions */}
      {grid && grid.rows.length > 0 && (
        <div className="flex items-center justify-between bg-white border border-border rounded-xl px-4 py-3">
          <p className="text-sm text-sage">
            {owedStaffCount > 0
              ? <>{owedStaffCount} staff with a net balance to transfer, totalling <b className="text-terracotta">{money(grid.totals.net_owed)}</b></>
              : "All net balances are settled for this period."}
          </p>
          <Button onClick={() => setPayAllOpen(true)} disabled={owedStaffCount === 0} className="bg-forest hover:bg-forest-light text-white" data-testid="pay-all-btn">
            <Check className="h-4 w-4 mr-2" /> Pay All (Transfer)
          </Button>
        </div>
      )}

      {/* Ledger grid */}
      <div className="bg-white border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>
        ) : !grid || grid.rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-sage"><Wallet className="h-12 w-12 mb-4 opacity-50" /><p>No earnings in this period</p></div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="payroll-grid">
              <TableHeader>
                <TableRow className="bg-sand">
                  <TableHead className="table-header-cell">Therapist</TableHead>
                  <TableHead className="table-header-cell">Payout</TableHead>
                  <TableHead className="table-header-cell text-right">Sessions</TableHead>
                  <TableHead className="table-header-cell text-right">Gross Earnings</TableHead>
                  {grid.super_enabled && <TableHead className="table-header-cell text-right">Super</TableHead>}
                  <TableHead className="table-header-cell text-right">Deductions (Cash)</TableHead>
                  <TableHead className="table-header-cell text-right">Transfer Paid</TableHead>
                  <TableHead className="table-header-cell text-right">Net Owed</TableHead>
                  <TableHead className="table-header-cell" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {grid.rows.map((r) => {
                  const c = staffColor(r.staff_id);
                  return (
                    <TableRow key={r.staff_id} className="hover:bg-sand/30 cursor-pointer" onClick={() => openDetail(r)} data-testid={`payroll-row-${r.staff_id}`}>
                      <TableCell>
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: c.bg, color: c.text }}>{r.staff_name}</span>
                      </TableCell>
                      <TableCell><span className="text-xs text-sage">{r.payout_type === "cash_daily" ? "Cash Daily" : "Weekly"}</span></TableCell>
                      <TableCell className="text-right text-sm">{r.total_sessions}</TableCell>
                      <TableCell className="text-right text-sm font-semibold text-forest">
                        {money(r.gross_earnings)}
                        {r.top_up > 0 && <span className="block text-[10px] text-ochre">incl. {money(r.top_up)} top-up</span>}
                      </TableCell>
                      {grid.super_enabled && (
                        <TableCell className="text-right text-sm text-sage" data-testid={`payroll-super-${r.staff_id}`}>
                          {r.super_amount > 0 ? (r.super_mode === "deduct" ? `-${money(r.super_amount)}` : money(r.super_amount)) : "-"}
                        </TableCell>
                      )}
                      <TableCell className="text-right text-sm text-ochre">{r.cash_deductions > 0 ? `-${money(r.cash_deductions)}` : "-"}</TableCell>
                      <TableCell className="text-right text-sm text-sage">{r.transfer_paid > 0 ? `-${money(r.transfer_paid)}` : "-"}</TableCell>
                      <TableCell className={`text-right text-sm font-bold ${r.net_owed > 0 ? "text-terracotta" : "text-forest"}`}>{money(r.net_owed)}</TableCell>
                      <TableCell><ChevronRight className="h-4 w-4 text-sage" /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Drill-down */}
      <Dialog open={!!detailRow} onOpenChange={(o) => { if (!o) setDetailRow(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payroll-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">{detailRow?.staff_name}</DialogTitle>
            <DialogDescription>{format(range.from, "MMM d")} – {format(range.to, "MMM d, yyyy")} · {detailRow?.payout_type === "cash_daily" ? "Cash Daily" : "Weekly Transfer"}</DialogDescription>
          </DialogHeader>
          {detailRow && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="stat-card"><p className="text-xs text-sage">Gross Earnings</p><p className="text-lg font-bold text-forest" data-testid="detail-gross">{money(detailRow.gross_earnings)}</p></div>
                {detailRow.super_amount > 0 && (
                  <div className="stat-card" data-testid="detail-super">
                    <p className="text-xs text-sage">Super</p>
                    <p className="text-lg font-bold text-sage">{money(detailRow.super_amount)}</p>
                    <p className="text-[10px] text-sage/70">{detailRow.super_mode === "deduct" ? "deducted from net" : "employer-paid"}</p>
                  </div>
                )}
                <div className="stat-card"><p className="text-xs text-sage">Cash Paid Daily</p><p className="text-lg font-bold text-ochre" data-testid="detail-cash-daily">{money(detailRow.cash_deductions)}</p></div>
                <div className="stat-card"><p className="text-xs text-sage">Transfer Paid</p><p className="text-lg font-bold text-sage" data-testid="detail-transfer-paid">{money(detailRow.transfer_paid)}</p></div>
                <div className="stat-card"><p className="text-xs text-sage">Cash Paid Periodic</p><p className="text-lg font-bold text-sage" data-testid="detail-cash-periodic">{money(detailRow.settle_cash_paid)}</p></div>
                <div className="stat-card border-2 border-terracotta/30"><p className="text-xs text-sage">Net Owed</p><p className={`text-lg font-bold ${detailRow.net_owed > 0 ? "text-terracotta" : "text-forest"}`} data-testid="detail-net-owed">{money(detailRow.net_owed)}</p></div>
              </div>

              {/* Daily earnings breakdown */}
              <div>
                <h4 className="font-heading font-semibold text-forest mb-2 text-sm">Daily Earnings</h4>
                <div className="border border-border rounded-lg overflow-x-auto max-h-56">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-sand">
                        <TableHead className="table-header-cell">Date</TableHead>
                        <TableHead className="table-header-cell text-right">Hours</TableHead>
                        <TableHead className="table-header-cell text-right">Commission</TableHead>
                        <TableHead className="table-header-cell text-right">Top-up</TableHead>
                        <TableHead className="table-header-cell text-right">Gross</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailRow.daily.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center py-5 text-sage">No earnings</TableCell></TableRow>
                      ) : detailRow.daily.map((d, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{d.date}</TableCell>
                          <TableCell className="text-right text-sm">{d.shift_hours}h</TableCell>
                          <TableCell className="text-right text-sm">{money(d.commission)}</TableCell>
                          <TableCell className="text-right text-sm text-ochre">{d.top_up > 0 ? `+${money(d.top_up)}` : "-"}</TableCell>
                          <TableCell className="text-right text-sm font-medium">{money(d.gross)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Itemized deductions */}
              <div>
                <h4 className="font-heading font-semibold text-forest mb-2 text-sm">Deductions — Cash Paid Daily</h4>
                {detailRow.deduction_items.length === 0 ? (
                  <p className="text-sm text-sage/60">No daily cash payouts in this period.</p>
                ) : (
                  <div className="space-y-1" data-testid="deduction-items">
                    {detailRow.deduction_items.map((d, i) => (
                      <div key={i} className="flex items-center justify-between bg-ochre/10 rounded-lg px-3 py-1.5 text-sm">
                        <span className="text-sage">{d.date}</span>
                        <span className="text-xs text-sage">comm {money(d.commission)}{d.top_up > 0 ? ` + top-up ${money(d.top_up)}` : ""}</span>
                        <span className="font-bold text-ochre">-{money(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Dual-method settlement for net owed */}
              <div className="bg-sand rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-heading font-semibold text-forest text-sm">Settle Balance</h4>
                  <span className="text-sm text-sage">Net owed: <b className="text-terracotta">{money(detailRow.net_owed)}</b></span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-sage">Record Cash Paid ($)</Label>
                    <Input type="number" step="0.01" value={payForm.cash_amount} onChange={(e) => {
                      const cash = e.target.value;
                      const net = detailRow.net_owed;
                      const rem = Math.max(0, Math.round((net - (parseFloat(cash) || 0)) * 100) / 100);
                      setPayForm((p) => ({ ...p, cash_amount: cash, transfer_amount: String(rem) }));
                    }} placeholder="0.00" data-testid="payment-cash-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-sage">Record Bank Transfer ($)</Label>
                    <Input type="number" step="0.01" value={payForm.transfer_amount} onChange={(e) => {
                      const transfer = e.target.value;
                      const net = detailRow.net_owed;
                      const rem = Math.max(0, Math.round((net - (parseFloat(transfer) || 0)) * 100) / 100);
                      setPayForm((p) => ({ ...p, transfer_amount: transfer, cash_amount: String(rem) }));
                    }} placeholder="0.00" data-testid="payment-transfer-input" />
                  </div>
                </div>
                <Textarea value={payForm.notes} onChange={(e) => setPayForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Notes…" rows={2} />
                <Button onClick={handleRecordPayment} disabled={submitting} className="w-full bg-forest hover:bg-forest-light" data-testid="confirm-payment-btn">
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DollarSign className="h-4 w-4 mr-2" />} Record Settlement
                </Button>
              </div>

              {periodPayments.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-heading font-semibold text-forest text-sm">Transfers this period</h4>
                  {periodPayments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between bg-white border border-border rounded-lg px-3 py-2 text-sm" data-testid={`payment-${p.id}`}>
                      <span className="text-sage">{format(new Date(p.created_at), "MMM d")}</span>
                      <span className="font-bold text-forest">{money(p.total_paid)}</span>
                      <Button variant="ghost" size="sm" className="text-destructive h-7" onClick={() => handleDeletePayment(p.id)} data-testid={`delete-payment-${p.id}`}>Delete</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailRow(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={payAllOpen} onOpenChange={setPayAllOpen}>
        <AlertDialogContent data-testid="pay-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer all net balances?</AlertDialogTitle>
            <AlertDialogDescription>
              This records a bank-transfer payout of the net owed for <b>{owedStaffCount}</b> staff member{owedStaffCount === 1 ? "" : "s"} totalling
              {" "}<b>{money(grid?.totals.net_owed)}</b> for {format(range.from, "MMM d")} – {format(range.to, "MMM d, yyyy")}. Delete individual transfers to reverse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-pay-all-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePayAll} disabled={payingAll} className="bg-forest hover:bg-forest-light" data-testid="confirm-pay-all-btn">
              {payingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />} Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
